<template>
    <v-row>
        <v-col cols="12">
            <div class="text-subtitle-2 mb-2 text-grey d-flex align-center">
                {{ title }}
                <v-progress-circular v-if="!skills" indeterminate size="16" width="2" class="ml-2"
                    color="grey"></v-progress-circular>
                <v-spacer />
                <!-- Show/hide the amplification chips (Config > Skill Amplification) -->
                <v-btn v-if="hasAmp" size="x-small" variant="text" :color="showAmp ? 'primary' : 'grey'"
                    :prepend-icon="showAmp ? 'mdi-eye' : 'mdi-eye-off'" @click="showAmp = !showAmp"
                    :title="showAmp ? 'Hide skill amplification coverage' : 'Show skill amplification coverage'">
                    Amp
                </v-btn>
            </div>

            <!-- Active / passive split (passive = arcana procs: Blast, Flare, Veil, Redoubled Offensive); pets are left out -->
            <div v-if="passiveDamage > 0" class="mb-2">
                <div class="d-flex justify-space-between text-caption mb-1" style="gap: 12px">
                    <span v-for="part in splitParts" :key="part.label" class="text-white">
                        <span class="split-dot" :style="{ background: part.color }"></span>
                        {{ part.label }} {{ part.percent.toFixed(1) }}%
                        <span class="text-grey">({{ formatNumber(part.damage) }})</span>
                    </span>
                </div>
                <div class="split-bar">
                    <div v-for="part in splitParts" :key="part.label" class="split-part"
                        :style="{ width: `${part.percent}%`, background: part.color }"></div>
                </div>
            </div>

            <!-- Scrollable Container -->
            <div class="skill-list-container thin-scroll">
                <!-- Sticky Header -->
                <div class="text-caption text-grey font-weight-bold px-2 mb-1 align-center"
                    :style="{ display: 'grid', gridTemplateColumns: GRID, gap: '8px', position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#212121' }">
                    <div>Skill</div>
                    <div class="text-right">Total</div>
                    <div class="text-right">Count</div>
                    <div class="text-right">Avg</div>
                    <div class="text-right">Crit</div>
                    <div class="text-right">HPM</div>
                    <div class="text-right">Min</div>
                    <div class="text-right">Max</div>
                    <div class="text-right" title="Share of the player's damage">%</div>
                </div>

                <template v-for="section in sections" :key="section.label">
                    <div v-if="sections.length > 1" class="section-label text-caption text-grey px-2">
                        {{ section.label }}
                    </div>

                    <!-- Rows -->
                    <div v-for="skill in section.skills" :key="skill.id"
                        class="px-2 mb-1 overflow-hidden align-center skill-row" :style="{
                            display: 'grid',
                            gridTemplateColumns: GRID,
                            gap: '8px',
                            minHeight: '42px',
                            fontSize: '1.0rem',
                        }">
                        <!-- Damage relative to the top skill: a thin bar along the bottom, not a fill behind the text -->
                        <div class="skill-bar" :style="{ width: `${skill.barPercent}%`, background: getSkillColor(skill.colorIndex) }"></div>

                        <!-- Name, plus estimated triggers for a passive proc -->
                        <div class="text-white py-1" style="min-width: 0; text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            <div class="d-flex align-center text-truncate">
                                <img width="32" height="32" :src='getSkillIconUrl(skill.id, region)' class="mr-2 rounded-sm"
                                    onerror="this.style.display='none'" />
                                <span class="font-weight-bold text-truncate" :title="skill.name">{{ skill.name }}</span>
                            </div>
                            <div v-if="skill.triggers?.length" class="d-flex flex-wrap mt-1" style="gap: 4px">
                                <v-chip v-for="t in skill.triggers" :key="t.id" size="x-small"
                                    variant="tonal" color="secondary"
                                    :title="`Estimated trigger: ${t.name}, ${t.count} procs, ${formatNumber(Math.round(t.damage))} damage`">
                                    {{ t.name }}
                                    <span class="text-grey ml-1">{{ t.percent.toFixed(0) }}%</span>
                                </v-chip>
                            </div>
                            <!-- Amplifying status coverage (Config > Skill Amplification) -->
                            <div v-if="skill.amp && showAmp" class="d-flex flex-wrap align-center mt-1" style="gap: 4px">
                                <v-chip v-for="st in skill.amp.statuses" :key="st.key" size="x-small" variant="tonal"
                                    :color="ampColor(st.percent)"
                                    :title="`${st.name}${st.onTarget ? ' (on target)' : ''}: up for ${st.count} of ${skill.amp.hits} hits`">
                                    <img v-if="getStatusIconUrl" width="14" height="14" class="mr-1"
                                        :src="getStatusIconUrl(st.id, region)" onerror="this.style.display='none'" />
                                    {{ st.name }}<span v-if="st.onTarget" class="text-grey ml-1">(target)</span>
                                    <span class="font-weight-bold ml-1">{{ st.percent.toFixed(1) }}%</span>
                                </v-chip>
                                <v-chip v-if="skill.amp.all" size="x-small" variant="outlined" :color="ampColor(skill.amp.all.percent)"
                                    :title="`All ${skill.amp.statuses.length} statuses up together: ${skill.amp.all.count} of ${skill.amp.hits} hits`">
                                    All <span class="font-weight-bold ml-1">{{ skill.amp.all.percent.toFixed(1) }}%</span>
                                </v-chip>
                                <v-chip v-if="skill.amp.any" size="x-small" variant="outlined" color="grey"
                                    :title="`At least one status up: ${skill.amp.any.count} of ${skill.amp.hits} hits`">
                                    Any <span class="font-weight-bold ml-1">{{ skill.amp.any.percent.toFixed(1) }}%</span>
                                </v-chip>
                            </div>
                        </div>

                        <!-- Stats -->
                        <div class="text-right font-weight-bold text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            {{ formatNumber(skill.total) }}
                        </div>
                        <div class="text-right text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            {{ skill.count }}</div>
                        <div class="text-right text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            {{ formatNumber(skill.avg) }}</div>
                        <div class="text-right text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            <span v-if="skill.critable === false" class="text-grey" title="This skill's hits cannot crit">—</span>
                            <template v-else>{{ skill.critRate.toFixed(1) }}%</template>
                        </div>
                        <div class="text-right text-caption text-white"
                            style="font-size: 0.85rem !important; text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            {{ skill.hitsPerMinute.toFixed(1) }}</div>
                        <div class="text-right text-caption text-white"
                            style="font-size: 0.85rem !important; text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            {{ formatNumber(skill.min) }}</div>
                        <div class="text-right text-caption text-white"
                            style="font-size: 0.85rem !important; text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            {{ formatNumber(skill.max) }}</div>
                        <div class="text-right text-white" style="text-shadow: 0 1px 2px rgba(0,0,0,0.8);">
                            <div class="font-weight-bold">{{ skill.percent.toFixed(1) }}%</div>
                        </div>
                    </div>
                </template>
            </div>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue';

// Amp chips shown or hidden in every skill breakdown at once, remembered per browser.
const AMP_KEY = 'funnycharts.skillBreakdown.showAmp';
const showAmp = ref((() => { try { return localStorage.getItem(AMP_KEY) !== '0'; } catch { return true; } })());
watch(showAmp, v => { try { localStorage.setItem(AMP_KEY, v ? '1' : '0'); } catch { /* storage unavailable */ } });

const GRID = 'minmax(180px, 3fr) minmax(100px, 1.2fr) minmax(50px, 0.5fr) minmax(80px, 0.8fr) minmax(50px, 0.5fr) minmax(60px, 0.6fr) minmax(80px, 0.8fr) minmax(80px, 0.8fr) minmax(50px, 0.5fr)';

export default defineComponent({
    name: 'SkillBreakdown',
    props: {
        skills: {
            type: Array as () => any[],
            required: false // Can be null/undefined
        },
        title: {
            type: String,
            default: 'Skill Breakdown'
        },
        // Damage from active skills vs passive procs; the split view only
        // shows when there is passive damage.
        activeDamage: {
            type: Number,
            default: 0
        },
        passiveDamage: {
            type: Number,
            default: 0
        },
        // Damage from the player's pets (not marionettes), shown apart.
        petDamage: {
            type: Number,
            default: 0
        },
        formatNumber: {
            type: Function,
            required: true
        },
        getSkillIconUrl: {
            type: Function,
            required: true
        },
        getSkillColor: {
            type: Function,
            required: true
        },
        // Optional: status icons on the amplification chips.
        getStatusIconUrl: {
            type: Function,
            required: false
        },
        hexToRgba: {
            type: Function,
            required: true
        },
        region: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const splitParts = computed(() => {
            const total = props.activeDamage + props.passiveDamage;
            return [
                { label: 'Active', damage: props.activeDamage, color: 'rgba(100, 181, 246, 0.9)' },
                { label: 'Passive', damage: props.passiveDamage, color: 'rgba(255, 183, 77, 0.85)' },
            ].filter(p => p.damage > 0)
                .map(p => ({ ...p, percent: total > 0 ? (p.damage / total) * 100 : 0 }));
        });

        // Active and passive skills in separate sections; colors keep each
        // skill's rank in the full (damage-sorted) list.
        const sections = computed(() => {
            const all = (props.skills || []).map((s: any, i: number) => ({ ...s, colorIndex: i }));
            if (!(props.passiveDamage > 0) && !(props.petDamage > 0)) return [{ label: 'Skills', skills: all }];
            return [
                { label: 'Active', skills: all.filter(s => !s.passive && !s.pet) },
                { label: 'Passive (procs) - triggers are estimated from hit timing', skills: all.filter(s => s.passive) },
                { label: "Pets - auto attacks and pet skills, not counted in the player's crit rate", skills: all.filter(s => s.pet) },
            ].filter(s => s.skills.length > 0);
        });

        // Amplification coverage: green when (nearly) always up, red when rarely.
        const ampColor = (p: number) => (p >= 90 ? 'success' : p >= 60 ? 'warning' : 'error');

        const hasAmp = computed(() => (props.skills || []).some((s: any) => s.amp));

        return { GRID, splitParts, sections, ampColor, showAmp, hasAmp };
    }
});
</script>

<style scoped>
.skill-list-container {
    max-height: 60vh;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.thin-scroll::-webkit-scrollbar {
    width: 6px;
}

.thin-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
}

.skill-row {
    position: relative;
    background: rgba(0, 0, 0, 0.3);
}

.skill-row:hover {
    background: rgba(255, 255, 255, 0.05);
}

.skill-bar {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 3px;
    border-radius: 0 2px 2px 0;
    pointer-events: none;
}

.section-label {
    padding-top: 6px;
    padding-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.04rem;
}

.split-bar {
    display: flex;
    height: 6px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
}

.split-part {
    height: 100%;
}

.split-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 2px;
}
</style>
